/**
 * Main application entry point
 * @module app
 */

import "@/static/sass/app.scss"
import "@/utils/i18n"

import { Loader } from "@software-engineering/hivolution-frontend-utils"
import { AuthProvider } from "oidc-react"
import { lazy, Suspense } from "react"
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"
import { BrowserRouter, Route, Routes } from "react-router"

import Private from "@/utils/auth/components/Private"
import MainContent from "@/utils/components/MainContent"
import { doRedirect, redirectURL } from "@/utils/redirect"
import { bootstrapLocalStorage } from "@/utils/storage"
import store from "@/utils/store/main"

const Home = lazy(() => import("@/apps/home/routes"))
const OrganizationRoutes = lazy(() => import("@/apps/organization/routes"))
const ConfigurationRoutes = lazy(() => import("@/apps/configuration/routes"))

const container = document.getElementById("app")
const root = createRoot(container)

/**
 * OIDC configuration object
 * @type {object}
 * @property {string} authority - OIDC endpoint URL
 * @property {string} clientId - OIDC client ID
 * @property {string} redirectUri - Redirect URL after authentication
 * @property {Function} onSignIn - Callback function on sign in
 */
const oidcConfig = {
  authority: window.OIDC_ENDPOINT,
  clientId: window.OIDC_CLIENT_ID,
  redirectUri: redirectURL,
  onSignIn: doRedirect
}

bootstrapLocalStorage()

root.render(
  <AuthProvider {...oidcConfig}>
    <Provider store={store}>
      <Private>
        <BrowserRouter>
          <Routes>
            <Route element={<MainContent />}>
              <Route
                path="/*"
                element={
                  <Suspense fallback={<Loader />}>
                    <Home />
                  </Suspense>
                }
              />
              <Route
                path="organizations/*"
                element={
                  <Suspense fallback={<Loader />}>
                    <OrganizationRoutes />
                  </Suspense>
                }
              />
              <Route
                path="configuration/*"
                element={
                  <Suspense fallback={<Loader />}>
                    <ConfigurationRoutes />
                  </Suspense>
                }
              />
            </Route>
          </Routes>
        </BrowserRouter>
      </Private>
    </Provider>
  </AuthProvider>
)
